import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PlaceTypeEnum } from '@buspro/shared/common';
import { PlaceInListDto, PlacesApi } from '@buspro/web/apis';
import { ErrorDisplayComponent, ExtendedProblemDetail, useFormErrorHandler } from '@buspro/web/core';
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-assign-clerk-terminals-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzModalModule,
    NzFormModule,
    NzSelectModule,
    NzButtonModule,
    ErrorDisplayComponent,
  ],
  template: `
    <nz-modal
      [nzVisible]="visible"
      [nzTitle]="'Assign Terminals to ' + clerkName"
      [nzWidth]="600"
      (nzOnCancel)="onCancel()"
      [nzFooter]="null"
    >
      <ng-container *nzModalContent>
        <app-error-display
          *ngIf="showError()"
          [error]="errorDetails"
          [showRetry]="true"
          (retry)="onRetry()"
          (dismiss)="onDismissError()"
          class="mb-4"
        ></app-error-display>

        <form [formGroup]="form" (ngSubmit)="onSubmit()">
          <nz-form-item>
            <nz-form-label nzRequired>Terminals</nz-form-label>
            <nz-form-control nzErrorTip="Please select at least one terminal">
              <nz-select
                formControlName="terminalIds"
                nzMode="multiple"
                nzPlaceHolder="Select terminals this clerk will work on"
                nzShowSearch
                [nzLoading]="terminalsQuery.isLoading()"
              >
                <nz-option
                  *ngFor="let terminal of availableTerminals()"
                  [nzValue]="terminal.id"
                  [nzLabel]="terminal.name"
                ></nz-option>
              </nz-select>
            </nz-form-control>
          </nz-form-item>

          <div class="alert alert-info mb-4">
            <span class="fe fe-info"></span>
            <span>
              Clerks can be assigned to multiple terminals. They will handle bookings and operations
              for passengers traveling to these terminals.
            </span>
          </div>

          <div class="flex justify-end gap-2">
            <button type="button" class="btn btn-ghost" (click)="onCancel()">Cancel</button>
            <button
              type="submit"
              class="btn btn-primary"
              [disabled]="form.invalid || assignMutation.isPending()"
            >
              <span *ngIf="assignMutation.isPending()" class="loading loading-spinner"></span>
              Assign Terminals
            </button>
          </div>
        </form>
      </ng-container>
    </nz-modal>
  `,
})
export class AssignClerkTerminalsModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  private placesApi = inject(PlacesApi);
  private notification = inject(NzNotificationService);
  private logger = inject(NGXLogger);
  private formErrorHandler = useFormErrorHandler();

  @Input() busStop!: PlaceInListDto;
  @Input() clerkId!: string;
  @Input() clerkName!: string;
  @Input() currentAssignments: string[] = []; // Terminal IDs currently assigned
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() assignmentCompleted = new EventEmitter<void>();

  form!: FormGroup;
  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;

  // Query available terminals
  terminalsQuery = injectQuery(() => ({
    queryKey: ['terminals', this.busStop?.companyId],
    queryFn: () =>
      lastValueFrom(
        this.placesApi.placeControllerGetTenantPlaces({
          type: PlaceTypeEnum.Terminal,
        }),
      ),
    enabled: !!this.busStop?.companyId,
  }));

  availableTerminals = signal<PlaceInListDto[]>([]);

  assignMutation = injectMutation(() => ({
    mutationFn: (terminalIds: string[]) =>
      lastValueFrom(
        this.placesApi.placeControllerAssignClerkToTerminals({
          busStopId: this.busStop.id,
          clerkId: this.clerkId,
          body: { terminalIds },
        }),
      ),
    onSuccess: () => {
      this.notification.success('✅ Terminals Assigned', 'Clerk has been assigned to selected terminals.');
      this.clearErrors();
      this.onCancel();
      this.assignmentCompleted.emit();
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to assign terminals:', error);
      const result = this.formErrorHandler.handleFormError(error, {
        formName: 'assign-clerk-terminals',
      });
      this.showError.set(true);
      this.errorDetails = result.problemDetail;
    },
  }));

  ngOnInit() {
    this.form = this.fb.group({
      terminalIds: [this.currentAssignments, [Validators.required, Validators.minLength(1)]],
    });

    // Update available terminals when query succeeds
    const terminals = this.terminalsQuery.data();
    if (terminals) {
      this.availableTerminals.set(terminals);
    }
  }

  onSubmit() {
    if (this.form.valid) {
      this.assignMutation.mutate(this.form.value.terminalIds);
    }
  }

  onCancel() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.form.reset();
    this.clearErrors();
  }

  onRetry() {
    this.clearErrors();
    this.onSubmit();
  }

  onDismissError() {
    this.clearErrors();
  }

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }
}
